import React, { useEffect, useState } from "react";
import { Button, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { toast } from "react-toastify";
import { updateSubTask, deleteSubTask, getSubTaskById, getTeammember, CommentDTO, addComment, } from "../../Api/ApiUrlConst";
import UpdateIcon from '@mui/icons-material/Update';
import { useAuth } from "../../Context/AuthContext";
import CancelIcon from '@mui/icons-material/Cancel';
// CHANGE 1: Add parentTaskDueDate to the props
interface EditSubTaskComponentProps {
  subTaskId: number;
  onClose?: (refresh?: boolean) => void;
}

interface SubTaskUpdateDto {
  description: string;
  assignedToId: string;
  status: string;
  priority: string;
  percentCompleted: number;
  workHours: number;
  completionDate: string | null;
  remarks: string;
  issues: string;
  parentSubTaskId: number | null;
  SpentHours: number;
}

interface TeamMemberDTO {
  userID: string;
  name: string;
  department: string; // ✅ added
}


const statusOptions = [
  { value: "YetToStart", label: "Yet to Start" },
  { value: "WaitingForInput", label: "Waiting for Input" },
  { value: "", label: "" },
  { value: "ModellingTodo", label: "Modelling ToDo" },
  { value: "ModellingInProgress", label: "Modelling in Progress" },
  { value: "ModellingCompleted", label: "Modelling Completed" },
  { value: "", label: "" },
  { value: "DraftingTodo", label: "Drafting ToDo" },
  { value: "DraftingInProgress", label: "Drafting in Progress" },
  { value: "DraftingCompleted", label: "Drafting Completed" },
  { value: "", label: "" },
  { value: "QCTodo", label: "QC ToDo" },
  { value: "QCInProgress", label: "QC in Progress" },
  { value: "QCCompleted", label: "QC Completed" },
  { value: "", label: "" },
  { value: "ClientReviewTodo", label: "Client Review ToDo" },
  { value: "ClientReviewInProgress", label: "Client Review in Progress" },
  { value: "ClientReviewCompleted", label: "Client Review Completed" },
  { value: "", label: "" },
  { value: "FinalCorrectionTodo", label: "Final Correction ToDo" },
  { value: "FinalCorrectionInProgress", label: "Final Correction in Progress" },
  { value: "FinalCorrectionCompleted", label: "Final Correction Completed" },
  { value: "", label: "" },
  { value: "RevisionTodo", label: "Revision ToDo" },
  { value: "RevisionInProgress", label: "Revision in Progress" },
  { value: "RevisionCompleted", label: "Revision Completed" },
  { value: "", label: "" },
  { value: "Cancelled", label: "Cancelled" },
  { value: 'Issues', label: 'Issues' },
  { value: "OnHold", label: "On Hold" },
  { value: "Released", label: "Released" },
  { value: "NotInScope", label: "Not In Scope" },
  { value: "Completed", label: "Ready for Release" },
];


const EditSubTaskComponent: React.FC<EditSubTaskComponentProps> = ({ subTaskId, onClose }) => {
  const [subTask, setSubTask] = useState<SubTaskUpdateDto>({
    description: "",
    assignedToId: "",
    status: "",
    priority: "",
    percentCompleted: 0,
    workHours: 0,
    completionDate: null,
    remarks: "",
    issues: "",
    parentSubTaskId: null,
    SpentHours: 0,
  });
  const { department } = useAuth();
  const [teamMembers, setTeamMembers] = useState<TeamMemberDTO[]>([]);
  const [loading, setLoading] = useState(false); // For update
  const [deleting, setDeleting] = useState(false); // For delete
  const [error, setError] = useState<string | null>(null);
  const [onHoldModal, setOnHoldModal] = useState(false);
  const [onHoldReason, setOnHoldReason] = useState("");
  const [pendingStatusTask, setPendingStatusTask] = useState<any>(null);
  const [comments, setComments] = useState<CommentDTO[]>([]);
  const onCancel = () => {
    onClose?.(false);
  };



  useEffect(() => {
    const fetchSubTaskAndTeamMembers = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await getSubTaskById(subTaskId);
        const data = response.data;

        setSubTask({
          description: data.description || data.title || "",
          assignedToId: data.assignedToId || "",
          status: data.status || "",
          priority: data.priority || "",
          percentCompleted: data.percentCompleted || 0,
          workHours: data.workHours ?? 0,
          completionDate: data.completionDate
            ? data.completionDate.slice(0, 10)
            : null,
          remarks: data.remarks ?? "",
          issues: data.issues ?? "",
          parentSubTaskId: data.parentSubTaskId ?? null,
          SpentHours: data.spentHours ?? 0

        });


        const teamResponse = await getTeammember();
        const teamData = teamResponse.data?.$values || teamResponse.data || [];
        setTeamMembers(teamData);

      } catch (err: any) {
        const message =
          err.response?.data?.message ||
          err.message ||
          "Failed to fetch subtask";
        setError(message);
        toast.error(message);
      } finally {
        setLoading(false);
      }
    };

    fetchSubTaskAndTeamMembers();
  }, [subTaskId]);


  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;

    // 🔥 Percent Auto Mapping
    const statusPercentMap: Record<string, number> = {
      ModellingCompleted: 30,
      DraftingCompleted: 60,
      QCCompleted: 70,
      ClientReviewCompleted: 80,
      FinalCorrectionCompleted: 90,
      Released: 100,
    };

    setSubTask(prev => {
      let updated: any = {
        ...prev,
        [name]:
          name === "workHours" ||
            name === "percentCompleted" ||
            name === "SpentHours"
            ? Number(value) || 0
            : value,

      };

      // ------------------------------------------------
      // 🔥 Rule 1: When STATUS changes → Auto set % Done
      // ------------------------------------------------
      if (name === "status" && statusPercentMap[value] !== undefined) {
        updated.percentCompleted = statusPercentMap[value];

        // Auto completion date for final statuses
        if (value === "Released" || value === "Completed") {
          updated.completionDate = new Date().toISOString().slice(0, 10);
        }
      }

      // ------------------------------------------------
      // 🔥 Rule 2: If % entered = 100 → Auto Released
      // ------------------------------------------------
      if (name === "percentCompleted" && Number(value) === 100) {
        updated.status = "Released";
        updated.completionDate = new Date().toISOString().slice(0, 10);
      }

      // ------------------------------------------------
      // 🔥 Rule 3: If manually select Completed/Released → %=100
      // ------------------------------------------------
      if (name === "status") {
        const today = new Date().toISOString().slice(0, 10);

        if (
          value === "Released" ||
          value === "Completed" ||
          value === "All Completed" ||
          value === "Completed & Closed"
        ) {
          updated.percentCompleted = 100;
          updated.completionDate = today;
        }
      }

      return updated;
    });
  };
  const handleStatusChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    task: any
  ) => {

    const value = e.target.value;

    if (value === "OnHold") {

      setPendingStatusTask({
        task,
        status: value
      });

      setOnHoldReason("");
      setOnHoldModal(true);

      return;
    }

    handleChange(e);
  };

  const saveOnHoldReason = async () => {

    if (!onHoldReason.trim()) {
      toast.error("Reason is required");
      return;
    }

    try {

      // 1️⃣ SAVE COMMENT API
      const newComment = await addComment({
        entityType: "SubTask",
        entityId: subTaskId,
        content: onHoldReason.trim()
      });

      // 2️⃣ ADD COMMENT TO UI STATE
      setComments((prev: any) => [
        newComment,
        ...prev
      ]);

      // 3️⃣ CHANGE STATUS
      const fakeEvent = {
        target: {
          name: "status",
          value: pendingStatusTask.status
        }
      };

      handleChange(fakeEvent as any);

      toast.success("Status updated");

      setOnHoldModal(false);

    } catch (err: any) {

      console.error(err);

      toast.error(
        err?.response?.data?.message ||
        "Failed to save comment"
      );
    }
  };




  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setLoading(true);
    setError(null);

    try {
      const payload: SubTaskUpdateDto = {
        ...subTask,
        SpentHours: subTask.SpentHours ?? 0, // ✅ ensure sent
        completionDate: subTask.completionDate === "" ? null : subTask.completionDate,
        workHours: subTask.workHours ?? 0,
      };


      await updateSubTask(subTaskId, payload);
      toast.success("Subtask updated successfully");
      onClose?.(true);
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || "Update failed";
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Updated delete handler with confirmation and separate loading
  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete this subtask?")) return;
    setDeleting(true);
    setError(null);

    try {
      await deleteSubTask(subTaskId);
      toast.success("Subtask deleted successfully");
      onClose?.(true);
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || "Delete failed";
      setError(message);
      toast.error(message);
    } finally {
      setDeleting(false);
    }
  };

  const filteredTeamMembers = !department
    ? teamMembers
    : teamMembers.filter(member => member.department === department);

  if (loading) return <tr><td colSpan={10}>Loading...</td></tr>;
  if (error) return <tr><td colSpan={10} style={{ color: "red" }}>Error: {error}</td></tr>;

  return (
    <form onSubmit={handleSubmit}>
      <tr>
        <td style={{ width: '2%' }}></td>
        <td style={{ width: '28%' }}>
          <Input name="description" value={subTask.description} onChange={handleChange} required />
        </td>
        {/* <td style={{ width: '5%' }}><Input
          type="number"
          name="SpentHours"
          min="0"
          step="0.5"
          value={subTask.SpentHours}
          onChange={handleChange}
        />
        </td> */}
        <td style={{ width: '5%' }}>
          <Input name="percentCompleted" type="number" min="0" max="100" value={subTask.percentCompleted}
            //           onChange={(e) => {
            //       const newValue = Number(e.target.value) || 0;

            //       // prevent decrease
            //       if (newValue < (subTask.percentCompleted || 0)) return;

            //       handleChange(e);
            //     }}
            //      onKeyDown={(e) => {
            //   if (e.key === "ArrowDown") e.preventDefault();
            // }}
            onChange={handleChange}
          />

        </td>

        <td style={{ width: '12%' }}>
          <Input type="select" name="assignedToId" value={subTask.assignedToId} onChange={handleChange} required>
            <option value="">Select Team Member</option>
            {filteredTeamMembers.map((member) => (
              <option key={member.userID} value={member.userID}>{member.name}</option>
            ))}
          </Input>
        </td>
        <td style={{ width: '15%' }}>
          <Input type="select" name="status" value={subTask.status} onChange={(e) => handleStatusChange(e, subTask)} required>
            <option value="">Select Status</option>
            {statusOptions.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </Input>
        </td>
        {/* <td style={{ width: '10%' }}>
          <Input type="date" name="completionDate" value={subTask.completionDate ?? ""} onChange={handleChange} />
        </td> */}



        <td style={{ width: '12%' }}>
          <Input type="select" name="priority" value={subTask.priority} onChange={handleChange} required>
            <option value="">Select Priority</option>
            <option value="Low">Low</option>
            <option value="Medium">Medium</option>
            <option value="High">High</option>
            <option value="Critical">Critical</option>
          </Input>
        </td>


        <td className="text-center" style={{ width: '5%' }}>
          <div className="d-flex justify-content-center gap-2">

            {/* Update Button */}
            <Button
              color="primary"
              size="sm"
              type="submit"
              disabled={loading || deleting}
              className="d-flex align-items-center justify-content-center"
              style={{ width: "36px", height: "36px" }}
              title="Update"
            >
              {loading ? "..." : <UpdateIcon fontSize="small" />}
            </Button>

            {/* Cancel Button */}
            <Button
              color="secondary"
              size="sm"
              onClick={onCancel}
              className="d-flex align-items-center justify-content-center"
              style={{ width: "36px", height: "36px" }}
              title="Cancel"
            >
              <CancelIcon />
            </Button>

          </div>

        </td>
      </tr>


      <Modal
        isOpen={onHoldModal}
        toggle={() => setOnHoldModal(false)}
      >
        <ModalHeader toggle={() => setOnHoldModal(false)}>
          On Hold Reason
        </ModalHeader>

        <ModalBody>

          <FormGroup>
            <Label>Reason *</Label>

            <Input
              type="textarea"
              value={onHoldReason}
              onChange={(e) =>
                setOnHoldReason(e.target.value)
              }
              placeholder="Enter reason..."
            />
          </FormGroup>

        </ModalBody>

        <ModalFooter>

          <Button
            color="secondary"
            onClick={() => setOnHoldModal(false)}
          >
            Cancel
          </Button>

          <Button
            color="primary"
            onClick={saveOnHoldReason}
          >
            Save
          </Button>

        </ModalFooter>
      </Modal>


    </form>
  );
};


export default EditSubTaskComponent;
